import { faSearch } from "@jewlr/pro-regular-svg-icons/faSearch"
import { faTimes } from "@jewlr/pro-regular-svg-icons/faTimes"
import loadable from "@loadable/component"
import { FocusScope } from "@react-aria/focus"
import { useFocusWithin, useKeyboard } from "@react-aria/interactions"
import PropTypes from "prop-types"
import { useRef, useState, useEffect } from "react"
import Autosuggest from "react-autosuggest"
import { useNavigate } from "react-router-dom"
import { TouchScrollable } from "react-scrolllock-with-horizontal-scroll"

import withSearch from "areas/header/hoc/with-search"
import SimpleDeferAfterLoadWrapper from "components/common/simple-defer-load-wrapper"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import { IS_ENG, retry } from "helpers/application"
import { send_search_keyword, setSearchHistory } from "helpers/search"
import useHandleOutsideClick from "helpers/use-handle-outside-click"
import { cn } from "styles/utils"

const SearchSuggestions = loadable(() =>
  retry(() => import("./search-suggestions"))
)

const HeaderSearch = ({
  clearSearch,
  clearSearchSuggestions,
  getSuggestionValue,
  handleCloseSearch,
  isMobile,
  onChange,
  onSuggestionsFetchRequested,
  renderSuggestion,
  suggestions,
  toggleSearching,
  value,
}) => {
  const formRef = useRef()
  const searchInputRef = useRef()
  const searchButtonRef = useRef()
  const [searchShowing, setSearchShowing] = useState(false)
  const navigate = useNavigate()

  const closeSearchBar = () => handleCloseSearch()

  const openSearchBar = () => {
    setSearchShowing(true)
    toggleSearching(false)
  }

  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => openSearchBar(),
  })
  const { keyboardProps } = useKeyboard({
    onKeyDown: (e) => {
      if (e.key === "Escape") {
        closeSearchBar()
        searchButtonRef.current?.focus()
        e.preventDefault()
      } else if (e.key === "Tab" && e.shiftKey) {
        closeSearchBar()
        e.continuePropagation()
      }
    },
  })
  const { keyboardProps: searchButtonKeyboardProps } = useKeyboard({
    onKeyDown: (e) => {
      if (e.key === "Tab") {
        if (e.shiftKey) {
          openSearchBar()
        } else {
          closeSearchBar()
        }
      }
    },
  })

  const handleClearSearch = (e) => {
    e.stopPropagation()
    clearSearch()
    clearSearchSuggestions()
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (value.length === 0) {
      return
    }

    setSearchHistory(value)
    clearSearch()
    clearSearchSuggestions()

    let url = encodeURI(`/search?k=${value}`)
    navigate(url)
  }

  const containerProps = {
    "aria-expanded": searchShowing,
  }

  const inputProps = {
    name: "search",
    onChange: (e) => {
      if (!searchShowing) openSearchBar()
      onChange(e)
    },
    placeholder: `Search ${IS_ENG() ? "Jewlr" : ""}...`,
    ref: searchInputRef,
    type: "search",
    value,
  }

  const shouldRenderSuggestions = (value) => {
    return value.length > 1
  }

  const shouldRenderConfiguredSearch = (query) => {
    return (isMobile || searchShowing) && query.length < 3
  }

  const onSuggestionSelected = (event, { suggestion }) => {
    event.preventDefault()
    handleClearSearch(event)
    setSearchHistory(suggestion.query_string)
    send_search_keyword(suggestion.keyword, suggestion.url)
  }

  const renderSuggestionsContainer = ({ containerProps, children, query }) => (
    <TouchScrollable>
      <div {...{ ...containerProps, role: undefined }}>
        <SimpleDeferAfterLoadWrapper>
          <SearchSuggestions
            handleSearchHistorySelected={(e) => handleClearSearch(e)}
            query={query}
            searchShowing={searchShowing}
            shouldRenderConfiguredSearch={shouldRenderConfiguredSearch}
          >
            {children}
          </SearchSuggestions>
        </SimpleDeferAfterLoadWrapper>
      </div>
    </TouchScrollable>
  )

  useEffect(() => {
    if (searchInputRef?.current) {
      searchInputRef.current.focus()
    }
  }, [searchInputRef.current])

  useHandleOutsideClick({
    enabled: searchShowing,
    onOutsideClick: () => closeSearchBar(),
    ref: formRef,
  })

  return (
    <FocusScope contain>
      <form
        className={cn(
          "site-search-form",
          searchShowing
            ? "[&.react-autosuggest__suggestions-container]:block"
            : "[&.react-autosuggest__suggestions-container]:hidden"
        )}
        data-cy="header-search-form"
        onSubmit={handleSubmit}
        ref={formRef}
        {...focusWithinProps}
        {...keyboardProps}
      >
        <Autosuggest
          alwaysRenderSuggestions
          containerProps={containerProps}
          getSuggestionValue={getSuggestionValue}
          id="header-search"
          inputProps={inputProps}
          onSuggestionSelected={onSuggestionSelected}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          renderInputComponent={(inputProps) => (
            <div className="desktop:p-0 desktop:shadow-none h-(--header-menu-height) relative z-20 flex items-center gap-6 px-16 shadow-lg">
              <div className="relative flex flex-1">
                <input
                  data-cy="header-search-input"
                  {...inputProps}
                  className={cn(
                    "bg-grey-5 focus:outline-hidden text-16 focus:ring-primary rounded-4xl my-9 box-content h-40 w-full px-44 text-black focus:ring-2",
                    "desktop:relative desktop:right-0 desktop:h-auto desktop:w-100 desktop:rounded-none desktop:bg-white desktop:pl-18 desktop:pr-0 desktop:text-14 desktop:transition-[width] desktop:duration-200 desktop:ease-in-out desktop:focus:shadow-none desktop:focus:outline-2 desktop:focus:outline-offset-2 desktop:focus:outline-primary",
                    "desktop:placeholder:text-14 desktop:placeholder:font-light desktop:placeholder:not-italic desktop:placeholder:tracking-wide desktop:placeholder:text-grey-65",
                    searchShowing &&
                      "desktop:w-full desktop:rounded-4xl desktop:border-2 desktop:border-primary desktop:pb-8 desktop:pl-32 desktop:pr-24 desktop:pt-8 desktop:text-16 desktop:outline-offset-0 desktop:focus:outline-hidden desktop:focus-visible:outline-hidden"
                  )}
                />
                {inputProps?.value && (
                  <button
                    aria-label="Clear search"
                    className="desktop:right-12 desktop:rounded-none desktop:p-0 absolute right-0 top-1/2 z-20 -translate-y-1/2 rounded-[40px] p-12 focus:outline-offset-0"
                    onClick={(e) => handleClearSearch(e)}
                    type="reset"
                  >
                    <FontAwesomeIcon
                      className="desktop:text-12 text-18"
                      icon={faTimes}
                    />
                  </button>
                )}
                <FontAwesomeIcon
                  className="text-grey-30 desktop:hidden text-18 absolute left-0 top-1/2 mx-12 block -translate-y-1/2"
                  icon={faSearch}
                />
              </div>
              <button
                className="desktop:hidden text-16 block"
                onClick={() => closeSearchBar()}
                type="button"
              >
                Cancel
              </button>
            </div>
          )}
          renderSuggestion={renderSuggestion}
          renderSuggestionsContainer={renderSuggestionsContainer}
          shouldRenderSuggestions={shouldRenderSuggestions}
          suggestions={suggestions}
        />
        <button
          aria-label={searchShowing ? "Close search" : "Open search"}
          className="desktop:left-12 desktop:block absolute left-0 top-1/2 z-30 hidden -translate-y-1/2"
          onClick={() => (searchShowing ? closeSearchBar() : openSearchBar())}
          ref={searchButtonRef}
          type="button"
          {...searchButtonKeyboardProps}
        >
          <FontAwesomeIcon className="text-18" icon={faSearch} />
        </button>
      </form>
    </FocusScope>
  )
}

HeaderSearch.propTypes = {
  clearSearch: PropTypes.func,
  clearSearchSuggestions: PropTypes.func,
  getSuggestionValue: PropTypes.func,
  handleCloseSearch: PropTypes.func,
  isMobile: PropTypes.bool,
  onChange: PropTypes.func,
  onSuggestionsFetchRequested: PropTypes.func,
  renderSuggestion: PropTypes.func,
  suggestions: PropTypes.array,
  toggleSearching: PropTypes.func,
  value: PropTypes.string,
}

export default withSearch(HeaderSearch)
